// 261005 E3-P2b — thẻ khối chặng ④ Chia tập: danh sách lệnh + bảng trước/sau (ops) · bảng cũ/mới tóm tắt/móc + chi phí AI (rewrite) · "Lệnh không áp được" ·
// Xem thử / Áp / Khôi phục bản trước / Áp lại. Mọi quyết định hiển thị ở `splitBlockView.ts` (hàm thuần, có jest); file này chỉ vẽ.
import React from 'react';
import { Alert, Popconfirm, Tooltip, Typography } from 'antd';
import Button from '../WbButton';
import type { AssistantBlock } from '../../../../types/adaptationAssistant';
import { splitCardView } from './splitBlockView';
import { disabledTip } from './assistantView';
import { BlockBody, BlockFoot, BlockShell } from './BlockShell';
import AutoApplyLine from './AutoApplyLine';
import type { TimelineHandlers } from './AssistantTimeline'; // chỉ kiểu — không vòng phụ thuộc lúc chạy

const { Text } = Typography;

interface Props extends TimelineHandlers {
  block: AssistantBlock;
  headModifiedAt?: string | null;
  busy: boolean;
  acting: string | null;
  busyReason?: string | null;
}

const Lines: React.FC<{ title: string; lines: Array<{ ep: number; meta: string; synopsis: string; hook: string }> }> = ({ title, lines }) => (
  <div style={{ flex: '1 1 160px', minWidth: 0 }}>
    <Text type="secondary" style={{ fontSize: 13 }}>{title}</Text>
    {lines.map((l) => (
      <div key={l.ep} style={{ fontSize: 13, margin: '2px 0', wordBreak: 'break-word' }}>
        <b>Tập {l.ep}</b>{l.meta ? ` · ${l.meta}` : ''}
        {l.synopsis && <div style={{ color: 'var(--wb-text-2)' }}>{l.synopsis}</div>}
        {l.hook && <div style={{ color: 'var(--wb-text-3)' }}>Móc: {l.hook}</div>}
      </div>
    ))}
  </div>
);

const SplitBlockCard: React.FC<Props> = ({ block, headModifiedAt, busy, acting, busyReason, onPreview, onApply, onUndo, onReapply, onDismiss }) => {
  const v = splitCardView(block, busy || !!acting, headModifiedAt, busyReason);
  const why = busy && busyReason ? busyReason : undefined;
  const previewBtn = (
    <Button size="small" type="text" loading={acting === `preview-${block.id}`} onClick={() => onPreview(block)}>
      {v.previewLabel}{v.previewCost && !/tốn AI/i.test(v.previewLabel) ? ` · ${v.previewCost}` : ''}
    </Button>
  );
  return (
    <BlockShell>
      <BlockBody title={block.title} tone={v.tone} stateLabel={v.headline} running={v.phase === 'running'} chip={v.kind === 'rewrite' ? 'Dùng AI' : null}>
      {v.opLabels.length > 0 && <ul style={{ margin: '4px 0', paddingLeft: 18, fontSize: 13 }}>{v.opLabels.map((l, i) => <li key={i} style={{ wordBreak: 'break-word' }}>{l}</li>)}</ul>}
      {v.detail && <div style={{ fontSize: 13, color: 'var(--wb-text-2)', margin: '4px 0', wordBreak: 'break-word' }}>{v.detail}</div>}
      <AutoApplyLine note={v.autoNote} />
      {(v.before.length > 0 || v.after.length > 0) && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '6px 0' }}>
          {v.before.length > 0 && <Lines title="Trước" lines={v.before} />}
          <Lines title="Sau" lines={v.after} />
        </div>
      )}
      {v.rewriteRows.length > 0 && (
        <div style={{ margin: '6px 0' }}>
          {v.rewriteRows.map((r) => (
            <div key={r.ep} style={{ fontSize: 13, margin: '4px 0', wordBreak: 'break-word' }}>
              <b>Tập {r.ep}</b>
              <div style={{ color: 'var(--wb-text-3)' }}>Cũ: {r.before.synopsis}{r.before.hook ? ` · Móc: ${r.before.hook}` : ''}</div>
              <div>Mới: {r.after.synopsis}{r.after.hook ? ` · Móc: ${r.after.hook}` : ''}{r.after.suspense ? ` · Còn treo: ${r.after.suspense}` : ''}</div>
            </div>
          ))}
        </div>
      )}
      {v.touchedNote && <Alert style={{ margin: '6px 0' }} type="info" showIcon message={v.touchedNote} />}
      {v.rejected.length > 0 && (
        <Alert style={{ margin: '6px 0' }} type="info" showIcon message="Lệnh không áp được" description={v.rejected.map((r, i) => <div key={i} style={{ fontSize: 13 }}>{r.label}: {r.reason}</div>)} />
      )}
      </BlockBody>
      <BlockFoot>
        {v.canPreview && (v.kind === 'rewrite'
          ? <Popconfirm title="Cho AI viết lại?" description="Tốn AI (đọc nguyên văn các tập này). Ranh giới tập giữ nguyên; chưa ghi gì cho tới khi bạn bấm Áp." okText="Xem thử" cancelText="Thôi" onConfirm={() => onPreview(block)}>
            <Button size="small" type="text" loading={acting === `preview-${block.id}`}>{v.previewLabel}</Button>
          </Popconfirm>
          : previewBtn)}
        {!v.canPreview && (why || v.phase === 'stale') && (v.phase === 'proposed' || v.phase === 'restored' || v.phase === 'failed' || v.phase === 'stale') && (
          <Tooltip title={why || 'Tập đã đổi; tạo đề xuất mới trước khi xem thử.'}><Button size="small" type="text" disabled>{v.previewLabel}</Button></Tooltip>
        )}
        {v.phase === 'ready' && (
          <Popconfirm
            title="Áp đề xuất này?" description="Ghi vào chia tập (giữ nguyên trạng thái duyệt khung xương); bản cũ còn ở mục Phiên bản."
            okText="Áp" cancelText="Thôi" onConfirm={() => onApply(block)} disabled={!v.canApply}
          >
            <Tooltip title={disabledTip(v.canApply, why)}><Button size="small" type="primary" disabled={!v.canApply} loading={acting === `apply-${block.id}`}>Áp</Button></Tooltip>
          </Popconfirm>
        )}
        {v.phase === 'applied' && (
          <Tooltip title={disabledTip(v.canUndo, v.undoReason ?? v.undoHint)}>
            <span>
              <Popconfirm title="Khôi phục bản trước?" description={v.undoHint} okText="Khôi phục" cancelText="Thôi" onConfirm={() => onUndo(block)} disabled={!v.canUndo}>
                <Button size="small" type="text" disabled={!v.canUndo} loading={acting === `undo-${block.id}`}>Khôi phục bản trước</Button>
              </Popconfirm>
            </span>
          </Tooltip>
        )}
        {v.phase === 'restored' && (
          <Popconfirm title="Áp lại đề xuất này?" description="Không tốn AI; chỉ áp được khi chia tập chưa khác lúc đề xuất." okText="Áp lại" cancelText="Thôi" onConfirm={() => onReapply(block)} disabled={!v.canReapply}>
            <Tooltip title={disabledTip(v.canReapply, why)}><Button size="small" type="primary" disabled={!v.canReapply} loading={acting === `reapply-${block.id}`}>Áp lại</Button></Tooltip>
          </Popconfirm>
        )}
        {v.canDismiss && <Button size="small" type="text" loading={acting === `dismiss-${block.id}`} onClick={() => onDismiss(block)}>Bỏ</Button>}
      </BlockFoot>
    </BlockShell>
  );
};

export default SplitBlockCard;
